import { Component, Vue, Watch } from 'vue-property-decorator';
import { chain } from 'lodash';
import moment from 'moment';
import { CONTRIBUTION_LEVELS, PAGE_NAMES } from '@/constants';
import { ContributionLevel, UserDropLogDTO, UserProfileDTO } from '@/models';
import { fetchUserRedeemableDrops, fetchUserProfile } from '@/services';
import ContributionLevelIcon from '@/components/common/contribution-level-icon/ContributionLevelIcon.vue';
import PeerRatingRadarChart from './peer-rating-radar-chart/PeerRatingRadarChart.vue';
import InlineLoader from '@/components/common/loader/inline-loader/InlineLoader.vue';
import DropTeaSVG from '@/assets/images/drop-teal.svg';

interface RedeemableDropLogByYear {
  year: string;
  logs: UserDropLogDTO[];
}
enum UserDropType {
  PROJECT_DROP = 'PROJECT_DROP',
  REDEEM_DROP = 'REDEEM_DROP',
  REFUND_DROP = 'REFUND_DROP',
  REJECT_DROP = 'REJECT_DROP'
}

enum PeerRatingOption {
  thisPeriod,
  allTime
}

@Component({
  components: {
    ContributionLevelIcon,
    PeerRatingRadarChart,
    InlineLoader
  }
})
export default class Profile extends Vue {
  private loading = false;
  private userProfileData = {} as UserProfileDTO;
  private peerRatingOption = PeerRatingOption.thisPeriod;
  private DropTeaSVG = DropTeaSVG;
  private redeemableDrop = 0;
  private legends = [
    {
      label: 'My score',
      isActive: true,
      style: {
        backgroundColor: 'rgba(73, 160, 181, 0.4)',
        outlineColor: 'rgb(73, 160, 181)'
      }
    },
    {
      label: 'KMS average',
      isActive: true,
      style: {
        backgroundColor: 'rgba(236, 167, 68, 0.4)',
        outlineColor: 'rgb(236, 167, 68)'
      }
    }
  ];

  private readonly PEER_RATING_OPTION = PeerRatingOption;
  private readonly redeemableHeader = [
    {
      value: 'itemName',
      cellClass: 'profile__redeemable--cell'
    },
    {
      value: 'date',
      align: 'end',
      cellClass: 'profile__redeemable--cell'
    },
    {
      value: 'deltaDrops',
      align: 'end',
      cellClass: 'profile__redeemable--cell'
    }
  ];

  get currentLevel(): ContributionLevel {
    const { totalEarnedDrops = 0 } = this.userProfileData;
    const levels = Object.values(CONTRIBUTION_LEVELS);
    return levels.find(({ min, max }) => min <= totalEarnedDrops && totalEarnedDrops <= max) || CONTRIBUTION_LEVELS.EGG;
  }

  async setRedeemableDrop(): Promise<void> {
    const { data } = await fetchUserRedeemableDrops();
    this.redeemableDrop = data;
  }

  get redeemableDropLogByYears(): RedeemableDropLogByYear[] {
    const { userDropHistory = [] } = this.userProfileData;
    return chain(userDropHistory)
      .orderBy('date', ['desc'])
      .groupBy(({ date }) => new Date(date).getFullYear())
      .toPairs()
      .map(([year, logs]) => ({
        year,
        logs
      }))
      .orderBy('year', ['desc'])
      .value();
  }

  getDisplayDate(date: string): string {
    return moment(date).format('MMM DD');
  }

  getDropDeltaClass(value: number): string {
    return value < 0 ? 'error--text' : 'success--text';
  }

  getItemNameClass(item: UserDropLogDTO): string {
    return item.type === UserDropType.PROJECT_DROP ? '' : 'font-italic light-grey--text';
  }

  getItemNameDisplay(item: UserDropLogDTO): string {
    if (item.type === UserDropType.REFUND_DROP) {
      return 'Refund redeeming ' + item.itemName;
    }
    if (item.type !== UserDropType.PROJECT_DROP) {
      return item.deltaDrops > 0 ? 'Reject redeeming ' + item.itemName : 'Redeem ' + item.itemName;
    }
    return item.itemName;
  }

  goToRewardPage(): void {
    this.$router.push({ name: PAGE_NAMES.REWARDS });
  }

  changeLegendActive(label: string): void {
    const legendIndex = this.legends.findIndex(legend => legend.label === label);
    if (legendIndex !== -1) {
      this.legends[legendIndex].isActive = !this.legends[legendIndex].isActive;
    }
  }

  async created(): Promise<void> {
    this.loading = true;
    const userProfileResponse = await fetchUserProfile();
    this.userProfileData = userProfileResponse.data;
    await this.setRedeemableDrop();
    this.loading = false;
  }

  @Watch('userProfileData', { deep: true })
  onUserProfileDataChanged(): void {
    const { criteriaRatingAverageUserThisPeriod = {} } = this.userProfileData;
    if (Object.values(criteriaRatingAverageUserThisPeriod).every(value => value === 0)) {
      this.peerRatingOption = this.PEER_RATING_OPTION.allTime;
    }
  }
}
